import i18next from 'i18next';
import { createInviteSchema } from '@proref/schemas';

// Reads the *current* language at call time via the shared i18next singleton, rather than taking
// `t` as a parameter — the usual pattern for a plain module-level helper (see lib/clip-file.ts).
// That pattern doesn't fit here: this module's functions are called directly by its own unit
// tests with a fixed arity (`parseRosterPaste(text)`, no `t` arg), and changing their signature
// would mean editing those tests — which the i18n retrofit's test-safety rule forbids. The
// singleton is safe to read synchronously: `test/setup.ts` bootstraps `lib/i18n` globally before
// any test runs, and jsdom's default navigator language resolves to the same 'en' fallback the
// hardcoded strings these tests assert against were already written in.
const t = (key: string, opts?: Record<string, unknown>) => i18next.t(key, opts) as string;

// One parsed line, ready for the preview table. `error` is set when the line can't be invited as
// written — the row still shows, so the admin can see WHICH line to fix rather than being told
// "something in your paste is wrong".
export interface ParsedRosterRow {
  full_name: string;
  email: string;
  error?: string;
}

// A header line people paste along with their spreadsheet export. Matched loosely (either column
// order, either separator) so it's dropped rather than reported as a broken row.
function isHeaderLine(fields: string[]): boolean {
  const lower = fields.map((f) => f.toLowerCase());
  return lower.includes('email') && (lower.includes('name') || lower.includes('full name'));
}

// Turn an email's local part into a presentable name, for a line that gave only an address.
// The admin's own name always wins — this is a fallback, and it's visible in the preview so a
// wrong guess is corrected before anything is sent, not after.
function nameFromEmail(email: string): string {
  const local = email.split('@')[0] ?? '';
  const words = local
    .split(/[._\-+]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1));
  return words.join(' ') || email;
}

// Parse a pasted roster (or a dropped CSV's text) into invite rows.
//
// Deliberately forgiving about shape and strict about content: any of comma, semicolon or tab
// separates the fields, either column order works, and a line that is just an address is fine —
// because what admins actually have is a copied column out of a spreadsheet, not a schema. What
// it will NOT do is guess past a real problem: each row is validated with the SAME
// `createInviteSchema` the API uses, so a malformed address is flagged here, in the preview,
// rather than coming back as a server rejection after the rest of the batch has already sent.
export function parseRosterPaste(text: string): ParsedRosterRow[] {
  const rows: ParsedRosterRow[] = [];

  for (const rawLine of text.split(/\r?\n/)) {
    const line = rawLine.trim();
    if (!line) continue;

    const fields = line
      .split(/[,;\t]/)
      .map((f) => f.trim().replace(/^["']|["']$/g, ''))
      .filter(Boolean);
    if (fields.length === 0) continue;
    if (isHeaderLine(fields)) continue;

    // The address is whichever field has an @ — that's what makes column order irrelevant.
    const emailIndex = fields.findIndex((f) => f.includes('@'));
    if (emailIndex === -1) {
      rows.push({ full_name: line, email: '', error: t('admin.users.rosterPaste.noEmailOnLine') });
      continue;
    }

    const email = fields[emailIndex] ?? '';
    const name = fields.filter((_, i) => i !== emailIndex).join(' ').trim();
    rows.push(checkInviteRow(name, email));
  }

  return rows;
}

// Validate one name + address pair, whatever typed them — a parsed CSV line or the two fields of
// the invite form. Uses the server's own schema so the form can't disagree with the API, and
// applies its transforms (trim, lower-case the address) so what's shown is exactly what is sent.
// A blank name falls back to one derived from the address, which is why the form doesn't demand it.
export function checkInviteRow(name: string, email: string): ParsedRosterRow {
  const trimmedEmail = email.trim();
  const full_name = name.trim() || nameFromEmail(trimmedEmail);

  if (!trimmedEmail) {
    return { full_name: name.trim(), email: '', error: t('admin.users.rosterPaste.noEmail') };
  }

  const parsed = createInviteSchema.safeParse({ full_name, email: trimmedEmail, role: 'referee' });
  if (!parsed.success) {
    const issue = parsed.error.issues[0];
    return {
      full_name,
      email: trimmedEmail,
      error:
        issue?.path[0] === 'email'
          ? t('admin.users.rosterPaste.invalidEmail')
          : (issue?.message ?? t('admin.users.rosterPaste.invalid')),
    };
  }

  return { full_name: parsed.data.full_name, email: parsed.data.email };
}

// Rows repeated inside one paste, by email. Flagged in the preview rather than sent and rejected:
// the API reports them as DUPLICATE_IN_BATCH, but the admin can see it here before spending a
// send, and the count they're shown then matches what actually goes out.
export function markDuplicates(rows: ParsedRosterRow[]): ParsedRosterRow[] {
  const seen = new Set<string>();
  return rows.map((row) => {
    if (row.error || !row.email) return row;
    if (seen.has(row.email)) return { ...row, error: t('admin.users.rosterPaste.listedTwice') };
    seen.add(row.email);
    return row;
  });
}
